import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MagnifyingGlass, X } from '@phosphor-icons/react'
import { useSearch } from '../hooks/useSearch'

export default function SearchBar() {
  const navigate = useNavigate()
  const { query, setQuery, results, loading, debouncedSearch } = useSearch()
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setQuery(value)
      setHighlightedIndex(-1)
      if (value.trim()) {
        setIsOpen(true)
        debouncedSearch(value)
      } else {
        setIsOpen(false)
      }
    },
    [setQuery, debouncedSearch]
  )

  const handleSelect = useCallback(
    (result: (typeof results)[number]) => {
      setIsOpen(false)
      setQuery('')
      navigate(result.path)
    },
    [navigate, setQuery]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : 0
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : results.length - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < results.length) {
            handleSelect(results[highlightedIndex])
          } else if (query.trim()) {
            setIsOpen(false)
            navigate(`/search?q=${encodeURIComponent(query.trim())}`)
          }
          break
        case 'Escape':
          setIsOpen(false)
          inputRef.current?.blur()
          break
      }
    },
    [isOpen, results, highlightedIndex, handleSelect, query, navigate]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="search-bar">
      <div className="search-bar__input-wrapper">
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="search-bar__icon"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setIsOpen(true)}
          placeholder="Search modules..."
          aria-label="Search modules"
          aria-autocomplete="list"
          aria-controls={isOpen ? 'search-results-listbox' : undefined}
          aria-expanded={isOpen}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `search-result-${results[highlightedIndex]?.id}`
              : undefined
          }
          className="search-bar__input"
        />
        {query && (
          <button
            type="button"
            className="search-bar__clear"
            onClick={() => {
              setQuery('')
              setIsOpen(false)
              inputRef.current?.focus()
            }}
            aria-label="Clear search"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {isOpen && (
        <div
          id="search-results-listbox"
          role="listbox"
          className="search-bar__dropdown"
        >
          {loading && results.length === 0 && (
            <div className="search-bar__empty">Searching...</div>
          )}

          {!loading && results.length === 0 && query.trim() && (
            <div className="search-bar__empty">
              No results for "{query}"
              <br />
              <button
                className="search-bar__view-all"
                onClick={() => {
                  setIsOpen(false)
                  navigate(`/search?q=${encodeURIComponent(query.trim())}`)
                }}
              >
                View all results
              </button>
            </div>
          )}

          {results.map((result, index) => (
            <div
              key={result.id}
              id={`search-result-${result.id}`}
              role="option"
              aria-selected={index === highlightedIndex}
              className={`search-bar__result ${
                index === highlightedIndex ? 'search-bar__result--highlighted' : ''
              }`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="search-bar__result-title">{result.title}</div>
              <div className="search-bar__result-meta">
                {result.crew} · {result.teams.join('/')} · {result.phase}
              </div>
              {result.snippet && (
                <div className="search-bar__result-snippet">
                  {result.snippet}
                </div>
              )}
            </div>
          ))}

          {results.length > 0 && (
            <div className="search-bar__footer">
              <button
                className="search-bar__view-all"
                onClick={() => {
                  setIsOpen(false)
                  navigate(`/search?q=${encodeURIComponent(query.trim())}`)
                }}
              >
                View all results →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
